import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@/shared.module';
import { NnnService } from '@/services';
import type { NicInterventieSummary, NnnRegistrationIntervention } from '@/services';
import type {
  EditNnnInterventionDialogData,
  EditNnnInterventionDialogResult,
  SaveState,
} from '../../types/careplan-nnn.types';
import { reuseOrCreateByRef } from '../../utils/reuse-or-create-by-ref';
import { EditNnnInterventionDialogComponent } from '../edit-nnn-intervention-dialog/edit-nnn-intervention-dialog.component';
import { NnnInterventionsInfoDialogComponent } from '../nnn-interventions-info-dialog/nnn-interventions-info-dialog.component';
import { NnnNotesCellComponent } from '../nnn-notes-cell/nnn-notes-cell.component';

/**
 * Structured NIC block of a careplan-NNN row: the picked interventies as
 * collapsible panels, each with a small line per selected activity, its own
 * notes textarea and edit / remove actions inside the panel. Emits the
 * updated `interventions` array; the parent owns the row and persists it.
 */
@Component({
  selector: 'app-nnn-interventions-section',
  standalone: true,
  imports: [SharedModule, NnnNotesCellComponent],
  templateUrl: './nnn-interventions-section.component.html',
  styleUrl: './nnn-interventions-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NnnInterventionsSectionComponent {
  private readonly nnnService = inject(NnnService);
  private readonly dialog = inject(MatDialog);

  readonly interventions = input.required<NnnRegistrationIntervention[]>();
  readonly diagnoseCode = input<number | null>(null);
  readonly nicIndex = input<Map<string, NicInterventieSummary>>(new Map());
  readonly editable = input(false);
  readonly saveState = input<SaveState>('');

  readonly interventionsChange = output<NnnRegistrationIntervention[]>();

  private readonly activityLabels = signal<ReadonlyMap<string, string>>(new Map());
  private readonly fetchedInterventies = new Set<number>();

  constructor() {
    effect(() => {
      for (const intervention of this.interventions()) {
        const interventie = this.nicIndex().get(intervention.nnnInterventionDbRef);
        if (interventie && !this.fetchedInterventies.has(interventie.code)) {
          this.fetchedInterventies.add(interventie.code);
          void this.loadActivityLabels(interventie.code);
        }
      }
    });
  }

  private async loadActivityLabels(interventieCode: number): Promise<void> {
    const { data } = await this.nnnService.getNicActiviteiten(interventieCode);
    if (data) {
      this.activityLabels.update(labels => {
        const next = new Map(labels);
        for (const activiteit of data) {
          next.set(String(activiteit.code), activiteit.omschrijving);
        }
        return next;
      });
    }
  }

  protected interventie(intervention: NnnRegistrationIntervention): NicInterventieSummary | undefined {
    return this.nicIndex().get(intervention.nnnInterventionDbRef);
  }

  protected activityLabel(ref: string): string {
    return this.activityLabels().get(ref) ?? ref;
  }

  protected async openAdd(): Promise<void> {
    const data: EditNnnInterventionDialogData = {
      excludeRefs: this.interventions().map(i => i.nnnInterventionDbRef),
      diagnoseCode: this.diagnoseCode(),
    };
    const chosen = await this.openDialog(data);
    if (!chosen) {
      return;
    }
    const added: NnnRegistrationIntervention = {
      id: null,
      nnnInterventionDbRef: String(chosen.interventie.code),
      notes: null,
      activities: chosen.activityRefs.map(ref => ({ id: null, nnnActivityDbRef: ref })),
    };
    this.cacheLabels(chosen);
    this.interventionsChange.emit([...this.interventions(), added]);
  }

  protected async openEdit(intervention: NnnRegistrationIntervention): Promise<void> {
    const interventie = this.interventie(intervention);
    if (!interventie) {
      return;
    }
    const data: EditNnnInterventionDialogData = {
      interventie,
      selectedActivityRefs: intervention.activities.map(activity => activity.nnnActivityDbRef),
    };
    const chosen = await this.openDialog(data);
    if (!chosen) {
      return;
    }
    const updated: NnnRegistrationIntervention = {
      ...intervention,
      activities: reuseOrCreateByRef(
        chosen.activityRefs,
        intervention.activities,
        activity => activity.nnnActivityDbRef,
        ref => ({ id: null, nnnActivityDbRef: ref })
      ),
    };
    this.cacheLabels(chosen);
    this.interventionsChange.emit(this.interventions().map(i => (i === intervention ? updated : i)));
  }

  protected openInfo(): void {
    this.dialog.open(NnnInterventionsInfoDialogComponent, {
      width: '720px',
      panelClass: 'card-primary',
    });
  }

  protected remove(intervention: NnnRegistrationIntervention): void {
    this.interventionsChange.emit(this.interventions().filter(i => i !== intervention));
  }

  protected onNotesInput(intervention: NnnRegistrationIntervention, value: string): void {
    intervention.notes = value;
  }

  protected saveNotes(): void {
    this.interventionsChange.emit([...this.interventions()]);
  }

  private openDialog(data: EditNnnInterventionDialogData): Promise<EditNnnInterventionDialogResult | undefined> {
    const dialogRef = this.dialog.open(EditNnnInterventionDialogComponent, {
      width: data.interventie ? '50%' : '80%',
      panelClass: ['card-primary', 'nnn-edit-dialog-panel'],
      data,
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  private cacheLabels(chosen: EditNnnInterventionDialogResult): void {
    this.activityLabels.update(labels => {
      const next = new Map(labels);
      for (const activiteit of chosen.activiteiten) {
        next.set(String(activiteit.code), activiteit.omschrijving);
      }
      return next;
    });
    this.fetchedInterventies.add(chosen.interventie.code);
  }
}
